import { PHORIA_QUALITY_COLOR, STATUS_COLOR } from "@constants/resultColors";
import { ObjectivePhoriaConditionSummary, ObjectivePhoriaResult } from "../../../types/testTypes";

// ─── Formatters ───────────────────────────────────────────────────────────────

export function formatPhoriaPd(value?: number | null): string {
  if (!Number.isFinite(value)) return "—";
  return `${Math.abs(Number(value)).toFixed(2)} PD`;
}

// ─── Colour helpers ───────────────────────────────────────────────────────────

export function getPhoriaColor(value?: number | null): string {
  if (!Number.isFinite(value)) return STATUS_COLOR.unavailable;
  const abs = Math.abs(Number(value));
  if (abs < 1) return STATUS_COLOR.good;
  if (abs < 6) return STATUS_COLOR.cautionYellow;
  return STATUS_COLOR.bad;
}

// Quality flag tokens — a single source of truth that both the lazy-eye
// summary card and the dedicated phoria detail page drive their UI from.
// "unstable_direction" typically reflects a near-zero phoria whose sign
// flips cycle-to-cycle. That's a feature of orthophoria, not a broken
// measurement, so it lives in its own neutral bucket — not "caution".
export type PhoriaQualityToken =
  | "good"
  | "near_orthophoria"
  | "no_strong_tendency"
  | "caution"
  | "unknown";

export function phoriaQualityToken(flag?: string): PhoriaQualityToken {
  const n = (flag ?? "").toLowerCase();
  if (n === "ok") return "good";
  if (n === "near_orthophoria") return "near_orthophoria";
  if (n === "unstable_direction") return "no_strong_tendency";
  if (n === "caution") return "caution";
  return "unknown";
}

const QUALITY_LABEL: Record<PhoriaQualityToken, string> = {
  good: "Good Quality",
  near_orthophoria: "Near Orthophoria",
  no_strong_tendency: "Near Zero",
  caution: "Caution",
  unknown: "N/A",
};

const QUALITY_DESCRIPTION: Record<PhoriaQualityToken, string> = {
  good: "Measurements were stable and consistent across cycles.",
  near_orthophoria: "Phoria magnitude is small but pointed in a consistent direction.",
  no_strong_tendency: "Phoria so close to zero that its eso/exo direction flipped between cycles.",
  caution: "Readings varied enough that the result should be interpreted carefully.",
  unknown: "Quality flag not available.",
};

export function getPhoriaQualityColor(flag?: string): string {
  return PHORIA_QUALITY_COLOR[phoriaQualityToken(flag)];
}

export function getPhoriaQualityLabel(flag?: string): string {
  return QUALITY_LABEL[phoriaQualityToken(flag)];
}

export function getPhoriaQualityDescription(flag?: string): string {
  return QUALITY_DESCRIPTION[phoriaQualityToken(flag)];
}

// ─── Magnitude & direction tokens ─────────────────────────────────────────────
// Single source of truth for band edges. Callers translate tokens to their
// own labels (English here, i18n on the detail page).

export type HorizontalMagnitudeToken =
  | "not_measurable"
  | "near_orthophoria"
  | "mild"
  | "moderate"
  | "large";

export type VerticalMagnitudeToken = "not_measurable" | "minimal" | "small" | "notable";

export type HorizontalDirectionToken = "not_available" | "near_orthophoria" | "eso" | "exo";

export type VerticalDirectionToken = "not_available" | "minimal" | "left_hyper" | "right_hyper";

export function horizontalMagnitudeToken(value?: number | null): HorizontalMagnitudeToken {
  if (!Number.isFinite(value)) return "not_measurable";
  const abs = Math.abs(Number(value));
  if (abs < 1) return "near_orthophoria";
  if (abs < 6) return "mild";
  if (abs < 10) return "moderate";
  return "large";
}

export function verticalMagnitudeToken(value?: number | null): VerticalMagnitudeToken {
  if (!Number.isFinite(value)) return "not_measurable";
  const abs = Math.abs(Number(value));
  if (abs < 1) return "minimal";
  if (abs < 2) return "small";
  return "notable";
}

export function horizontalDirectionToken(value?: number | null): HorizontalDirectionToken {
  if (!Number.isFinite(value)) return "not_available";
  if (Math.abs(Number(value)) < 1) return "near_orthophoria";
  return Number(value) > 0 ? "eso" : "exo";
}

export function verticalDirectionToken(value?: number | null): VerticalDirectionToken {
  if (!Number.isFinite(value)) return "not_available";
  if (Math.abs(Number(value)) < 1) return "minimal";
  return Number(value) > 0 ? "left_hyper" : "right_hyper";
}

// ─── English labels (used by the summary card) ────────────────────────────────

const H_MAGNITUDE_LABEL: Record<HorizontalMagnitudeToken, string> = {
  not_measurable: "Not measurable",
  near_orthophoria: "Near orthophoria",
  mild: "Mild",
  moderate: "Moderate",
  large: "Large",
};

const V_MAGNITUDE_LABEL: Record<VerticalMagnitudeToken, string> = {
  not_measurable: "Not measurable",
  minimal: "Minimal",
  small: "Small",
  notable: "Notable",
};

const H_DIRECTION_LABEL: Record<HorizontalDirectionToken, string> = {
  not_available: "N/A",
  near_orthophoria: "Orthophoria",
  eso: "Esophoria",
  exo: "Exophoria",
};

const V_DIRECTION_LABEL: Record<VerticalDirectionToken, string> = {
  not_available: "N/A",
  minimal: "Minimal",
  left_hyper: "Left Hyperphoria",
  right_hyper: "Right Hyperphoria",
};

export function classifyHorizontalMagnitude(value?: number | null): string {
  return H_MAGNITUDE_LABEL[horizontalMagnitudeToken(value)];
}

export function classifyVerticalMagnitude(value?: number | null): string {
  return V_MAGNITUDE_LABEL[verticalMagnitudeToken(value)];
}

export function getHorizontalDirection(value?: number | null): string {
  return H_DIRECTION_LABEL[horizontalDirectionToken(value)];
}

export function getVerticalDirection(value?: number | null): string {
  return V_DIRECTION_LABEL[verticalDirectionToken(value)];
}

/**
 * Returns a single descriptor combining magnitude + direction without repeating
 * the same word when both halves collapse to "essentially zero":
 *   1.51 PD horizontal  →  "Mild · Esophoria"
 *   0.30 PD horizontal  →  "Orthophoria"
 *   5.24 PD vertical    →  "Notable · Left Hyperphoria"
 *   0.40 PD vertical    →  "Minimal"
 */
export function describeHorizontal(value?: number | null): string {
  if (!Number.isFinite(value)) return "Not measurable";
  if (Math.abs(Number(value)) < 1) return "Orthophoria";
  return `${classifyHorizontalMagnitude(value)} · ${getHorizontalDirection(value)}`;
}

export function describeVertical(value?: number | null): string {
  if (!Number.isFinite(value)) return "Not measurable";
  if (Math.abs(Number(value)) < 1) return "Minimal";
  return `${classifyVerticalMagnitude(value)} · ${getVerticalDirection(value)}`;
}

// ─── Near-distance shift ──────────────────────────────────────────────────────

// Typical AC/A produces a 3–5 PD convergent shift from distance to near.
// Anything in that range is normal; > 6 PD points at convergence excess
// (or insufficiency, depending on direction) — the clinically meaningful
// territory. Sub-2 PD shifts are noise.
export type PhoriaShiftMagnitude = "minimal" | "typical" | "notable" | "marked";

export interface PhoriaShift {
  value: number;
  magnitude: PhoriaShiftMagnitude;
}

export function classifyPhoriaShift(value: number): PhoriaShiftMagnitude {
  const abs = Math.abs(value);
  if (abs < 2) return "minimal";
  if (abs < 6) return "typical";
  if (abs < 10) return "notable";
  return "marked";
}

export function getPhoriaShift(
  distance?: ObjectivePhoriaConditionSummary,
  near?: ObjectivePhoriaConditionSummary,
  occlusions?: ObjectivePhoriaResult["occlusions"],
): PhoriaShift | null {
  if (!Number.isFinite(distance?.horizontalPhoriaPd) || !Number.isFinite(near?.horizontalPhoriaPd))
    return null;
  // Shift only meaningful when both horizontal readings are reliable —
  // otherwise we'd be subtracting noise from noise.
  if (!isHorizontalReliable(distance, occlusions) || !isHorizontalReliable(near, occlusions))
    return null;
  const value = Number(near!.horizontalPhoriaPd) - Number(distance!.horizontalPhoriaPd);
  return { value, magnitude: classifyPhoriaShift(value) };
}

// ─── Spread indicator (passive UI hint) ───────────────────────────────────────

/**
 * Classifies an IQR (or SD) value in PD into a visual severity band used only
 * for coloring the spread line in the card. This is a passive cue — it never
 * suppresses values or claims a reading is "unreliable". IQR is preferred
 * over SD because it ignores outliers; the device's reported magnitude is
 * already a robust median.
 */
export type PhoriaSpreadSeverity = "ok" | "elevated" | "high";

/**
 * Threshold (in PD) above which an axis is considered unreliable. IQR is the
 * preferred spread input — robust to outliers and unaffected by orthophoric
 * patients having tiny means. Above this, cycle-to-cycle readings disagree
 * enough that the magnitude/direction summary isn't trustworthy.
 */
export const PHORIA_UNRELIABLE_IQR_PD = 4;

export function classifyPhoriaSpread(value?: number | null): PhoriaSpreadSeverity {
  if (!Number.isFinite(value)) return "ok";
  const v = Math.abs(Number(value));
  if (v > PHORIA_UNRELIABLE_IQR_PD) return "high";
  if (v > 2) return "elevated";
  return "ok";
}

/**
 * Reliability of a single axis based on IQR. Direction (eso/exo, hyper) and
 * magnitude labels should only be displayed when this returns true.
 */
export function isAxisReliable(iqrPd?: number | null): boolean {
  if (!Number.isFinite(iqrPd)) return true; // no IQR available → don't penalize
  return Math.abs(Number(iqrPd)) <= PHORIA_UNRELIABLE_IQR_PD;
}

// ─── Outlier-trimmed IQR ─────────────────────────────────────────────────────
//
// A single bad cycle out of 6 (blink, fixation loss, momentary occlusion) is
// enough to push the device-reported IQR over the 4 PD reliability gate even
// when the other five cycles agree. Empirical analysis of the Brockton
// screening dataset showed ~2/3 of axis-unreliable flags fit this single-
// outlier pattern. We therefore recompute IQR from the raw per-cycle
// occlusions after dropping the cycle whose deviation from the median is
// largest — and use that trimmed IQR for the reliability gate. The
// displayed IQR in any UI is still the device's original value.

function percentile(sortedAsc: number[], p: number): number {
  if (sortedAsc.length === 0) return Number.NaN;
  if (sortedAsc.length === 1) return sortedAsc[0];
  const rank = (sortedAsc.length - 1) * p;
  const lo = Math.floor(rank);
  const hi = Math.ceil(rank);
  if (lo === hi) return sortedAsc[lo];
  return sortedAsc[lo] + (rank - lo) * (sortedAsc[hi] - sortedAsc[lo]);
}

function rawIqr(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  return percentile(sorted, 0.75) - percentile(sorted, 0.25);
}

/**
 * IQR after dropping the single cycle farthest from the median. Returns
 * NaN for fewer than 3 values (no meaningful trim possible).
 */
export function trimmedIqr(values: number[]): number {
  if (values.length < 3) return Number.NaN;
  const sorted = [...values].sort((a, b) => a - b);
  const median = percentile(sorted, 0.5);
  let worstIndex = 0;
  let worstDistance = -1;
  for (let i = 0; i < values.length; i++) {
    const d = Math.abs(values[i] - median);
    if (d > worstDistance) {
      worstDistance = d;
      worstIndex = i;
    }
  }
  return rawIqr(values.filter((_, i) => i !== worstIndex));
}

type AxisDeltaKey = "horizontalDeltaPd" | "verticalDeltaPd";

function trimmedAxisIqrFromOcclusions(
  occlusions: ObjectivePhoriaResult["occlusions"] | undefined,
  conditionName: string,
  axisKey: AxisDeltaKey,
): number | null {
  if (!occlusions || occlusions.length === 0) return null;
  const values = occlusions
    .filter((o) => o?.conditionName === conditionName)
    .map((o) => Number(o[axisKey]))
    .filter((v) => Number.isFinite(v));
  if (values.length < 3) return null;
  const result = trimmedIqr(values);
  return Number.isFinite(result) ? result : null;
}

/**
 * The IQR our reliability gate should use. Prefers the trimmed value when
 * raw occlusions are available; otherwise falls back to the device's
 * reported summary IQR.
 */
export function effectiveHorizontalIqr(
  summary: ObjectivePhoriaConditionSummary | null | undefined,
  occlusions?: ObjectivePhoriaResult["occlusions"],
): number | null | undefined {
  if (!summary) return undefined;
  const trimmed = trimmedAxisIqrFromOcclusions(
    occlusions,
    summary.conditionName,
    "horizontalDeltaPd",
  );
  return trimmed ?? summary.horizontalIqrPd;
}

export function effectiveVerticalIqr(
  summary: ObjectivePhoriaConditionSummary | null | undefined,
  occlusions?: ObjectivePhoriaResult["occlusions"],
): number | null | undefined {
  if (!summary) return undefined;
  const trimmed = trimmedAxisIqrFromOcclusions(
    occlusions,
    summary.conditionName,
    "verticalDeltaPd",
  );
  return trimmed ?? summary.verticalIqrPd;
}

export function isHorizontalReliable(
  s?: ObjectivePhoriaConditionSummary | null,
  occlusions?: ObjectivePhoriaResult["occlusions"],
): boolean {
  if (!s) return false;
  return isAxisReliable(effectiveHorizontalIqr(s, occlusions));
}

export function isVerticalReliable(
  s?: ObjectivePhoriaConditionSummary | null,
  occlusions?: ObjectivePhoriaResult["occlusions"],
): boolean {
  if (!s) return false;
  return isAxisReliable(effectiveVerticalIqr(s, occlusions));
}

export function hasAnyUnreliableAxis(data: ObjectivePhoriaResult): boolean {
  return (data.conditionSummaries ?? []).some(
    (s) => !isHorizontalReliable(s, data.occlusions) || !isVerticalReliable(s, data.occlusions),
  );
}

// Risk-band math lives in lazyEyeRiskModel.ts — kept there so it can be
// age-aware and combine with other lazy-eye findings.

// ─── Clinical summary (one paragraph, patient-facing) ─────────────────────────

export function buildPhoriaClinicalSummary(
  distance?: ObjectivePhoriaConditionSummary,
  near?: ObjectivePhoriaConditionSummary,
  occlusions?: ObjectivePhoriaResult["occlusions"],
): string {
  const distHReliable = isHorizontalReliable(distance, occlusions);
  const nearHReliable = isHorizontalReliable(near, occlusions);
  const distVReliable = isVerticalReliable(distance, occlusions);

  const distText = distHReliable
    ? `${describeHorizontal(distance?.horizontalPhoriaPd).toLowerCase()} (${formatPhoriaPd(distance?.horizontalPhoriaPd)})`
    : "readings disagreed too much to interpret";
  const nearText = nearHReliable
    ? `${describeHorizontal(near?.horizontalPhoriaPd).toLowerCase()} (${formatPhoriaPd(near?.horizontalPhoriaPd)})`
    : "readings disagreed too much to interpret";

  let text = `At distance: ${distText}. At near: ${nearText}.`;

  const shift = getPhoriaShift(distance, near, occlusions);
  if (shift) {
    const formatted = formatPhoriaPd(shift.value);
    if (shift.magnitude === "minimal") {
      text += ` The near-distance shift is minimal (${formatted}).`;
    } else if (shift.magnitude === "typical") {
      text += ` The eyes shift by ${formatted} between distance and near — within the normal AC/A range.`;
    } else if (shift.magnitude === "notable") {
      text += ` The near-distance shift of ${formatted} is larger than typical — worth monitoring.`;
    } else {
      text += ` The near-distance shift of ${formatted} is unusually large and warrants further evaluation.`;
    }
  } else if (!distHReliable || !nearHReliable) {
    text += ` Near–distance shift can't be evaluated until both readings are reliable.`;
  }

  if (distVReliable) {
    const vertH = classifyVerticalMagnitude(distance?.verticalPhoriaPd);
    if (vertH !== "Minimal" && Number.isFinite(distance?.verticalPhoriaPd)) {
      text += ` A ${vertH.toLowerCase()} vertical deviation was also noted.`;
    }
  }

  return text;
}
